import React, {useEffect, useState} from 'react';

import FilePreview from 'files/preview/FilePreview';
import {fetchSignedFileUrl} from 'llm/chat-library/ChatDAO';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';

interface FileViewerBodyProps {
  chatSessionId: string;
  externalFileId: string;
  filename: string;
  mimeType: string;
}

function useChatFileSignedUrl(
  chatSessionId: string,
  externalFileId: string
): {
  signedUrl: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSignedUrl(null);
    setIsLoading(true);
    setError(null);
    fetchSignedFileUrl(chatSessionId, externalFileId)
      .then((url) => {
        if (!cancelled) {
          setSignedUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load file.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chatSessionId, externalFileId]);

  return {signedUrl, isLoading, error};
}

export function FileViewerBody({
  chatSessionId,
  externalFileId,
  filename,
  mimeType,
}: FileViewerBodyProps): JSX.Element {
  const {signedUrl, isLoading, error} = useChatFileSignedUrl(chatSessionId, externalFileId);

  if (isLoading) {
    return <CenteredSpinner />;
  }
  if (error || signedUrl == null) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error ?? 'Failed to load file.'} />;
  }

  return <FilePreview url={signedUrl} mimeType={mimeType} filename={filename} />;
}
